
import { Vue, Component, Watch } from 'vue-property-decorator';
import { Mutation, State, Getter, Action } from 'vuex-class';
import BytedIconAddOne from '@byte-design/vue-icons/icons/AddOne';
import BytedIconMore from '@byte-design/vue-icons/icons/More';
import BytedIconTipsOne from '@byte-design/vue-icons/icons/TipsOne';
import HeaderNav from '@/components/header-nav.vue';
import { isEmpty } from 'lodash';
import qs from 'qs';
import store from '@/store/index.ts';
import { countryRecharge } from '@/const/state/self-service-config';
import {
  recommendedAmount,
  recommendedAmountCLCLP,
  recommendedAmountPEPEN,
  recommendedAmountCOCOP,
  recommendedAmountSelectedCountryUSD,
  paymentTaxType,
  TeaConfigPayment,
  paymethodTypeMap,
  PaymentSource,
} from '@/const/payment';
import currencyMixin from '@/mixins/currencyMixin';
import HelpCenter from '@/components/help-center.vue';
import Balance from '@/components/balance.vue';
import LoadingButton from '@/components/loading/loading-button.vue';
import { canEdit, isOperatorOfAgent } from '@/utils/authUtil';

import { ifPaymentDisable } from '@/const/state/account';
import browerType from '@/utils/browserType';
import apis from '@/const/apis';
import { domainTTP, PAYMENT_SCENE } from '@/const/common';
import * as tool from '@/utils/tool';
import { getLang } from '@/utils/unicodeUtil';
import BytedIconHelp from '@byte-design/vue-icons/icons/Help';
import PopupHelp from './popup-help.vue';
import { formatNumberByLangCodeUtil } from '@/pages/dashboard/perf/utils';
import { XInputNumber } from '@okee-uikit/web-components-vue';
import { navigateToCreationObjectives } from '@/router/embedded';
@Component({
  name: 'Purchase',
  mixins: [currencyMixin],
  waitApiList: [apis.account.balance],
  components: {
    HeaderNav,
    HelpCenter,
    BytedIconAddOne,
    BytedIconMore,
    BytedIconTipsOne,
    Balance,
    LoadingButton,
    BytedIconHelp,
    PopupHelp,
    XInputNumber,
  },
})
export default class Purchase extends Vue {
  @State(state => state.account.accountInfo.id) aadvid: string;
  @Getter('account/isManagementByBC') isManagementByBC: boolean;
  @Getter('account/isSelfBcAccount') isSelfBcAccount: boolean;

  @State('chargeInfo', { namespace: 'payment' }) chargeInfo: any;
  @State('cardInfo', { namespace: 'payment' }) cardInfo: any;
  @State('billingABTest') billingABTest: boolean;
  @Getter('account/isNotBcChildSelfAccount') isNotBcChildSelfAccount: boolean;
  @Getter('account/accountInfo') accountInfo: any;
  @Getter('account/isWorldPayUser') isWorldPayUser: boolean;
  @Getter('payment/taxType') taxType: number;
  @Getter('payment/hasContract') hasContract: boolean;
  @Getter('payment/taxInfo') taxInfo: any;
  @Getter('payment/isActivePaSDK') isActivePaSDK: boolean;
  @Mutation('payment/SET_PAYMENT_PROPERTY') setProp: any;
  @Action('payment/subPrePay') subPrePay: any;
  @Action('payment/loadChargeTax') loadChargeTax: () => Promise<void>;
  @Action('payment/getCardBindLink') getCardBindLink: () => Promise<string>;
  @Action('payment/getPaymentAccount') getPaymentAccount: () => Promise<string>;
  @Action('payment/getCardInfo') getCardInfo: () => Promise<{
    identity: string;
  }>;

  amount = '';
  errorText = '';
  paying = false;
  showFavoriteDialog = false;
  showManagerCard = false;
  taxReady = false; // 新用户税率信息获取有延迟，如果税率不ready就block充值流程
  showHelp = false;
  showTaxTip = false;
  paymethodTypeMap = paymethodTypeMap;
  noTipTaxKey = ['GST/HST'];

  @Watch('amount')
  onAmountChanged(val) {
    this.errorText = this.checkAmount(val) || '';
  }

  beforeRouteEnter(to: any, from: any, next: any) {
    Promise.all([store.dispatch('payment/getChargeInfo'), store.dispatch('payment/getPaymentAccount')]).then(() => {
      // 是否激活pa
      const isActivePaSDK = store.getters['payment/isActivePaSDK'];

      if (isActivePaSDK) {
        // platform 8 标记ttam h5
        const query = qs.stringify({
          _pia_: 1,
          adv_id: this.aadvid,
          platform: 8,
          ...to.query,
        });
        window.location.href = `${location.origin}/wap/pa/purchase?${query}`;
        return;
      }

      const isPrepay = store.getters['payment/isPrepay'];
      const isPostpay = store.getters['payment/isPostpay'];
      if (isPostpay) {
        next({ name: 'credit_purchase', query: to.query });
        return;
      }
      if (!isPrepay) {
        next({ name: 'perf-dashboard-detail', query: to.query });
        return;
      }
      next();
    });
  }

  created() {
    // 进入purchase页面增加曝光埋点
    this.$trackEvent('adp_waplite_impression', {
      payment: 'add_balance_pageview',
    });
    // 消耗计税不去获取税率阻塞充值
    if (this.needPayTaxes) {
      this.loadChargeTax?.()
        .then(() => {
          this.taxReady = true;
        })
        .catch(() => {
          this.taxReady = false;
        });
    } else {
      this.taxReady = true;
    }

    // 查询绑卡信息
    this.getCardInfo().then(({ identity }) => {
      if (identity) {
        // 获取卡管理链接
        this.getCardBindLink();
      }
    });
  }

  get buttonText() {
    return this.isNotBcChildSelfAccount ? this.$t('common_back') : this.$t('common_cancel');
  }

  /**
   * 税率信息ready时再允许充值
   */
  get disableAddBalance() {
    return this.paying || !this.amount || !this.taxReady;
  }

  /**
   * 充值计税默认展示税种明细，有国家或地区存在多税种
   */
  get formatTaxInfo() {
    const amount = parseFloat((this.amount as any) || 0);
    return Object.entries(this.taxInfo).map(([key, rate]) => ({
      rate: (rate * 100).toFixed(3),
      tax: (amount * rate).toFixed(2),
      name: key,
    }));
  }

  /**
   * 充值计税模式且有税 显示税种信息
   * https://bytedance.feishu.cn/sheets/shtcnDoLpUw4JpsNnZt0ENj4jUS
   */
  get showTaxDetail() {
    return this.needPayTaxes && this.taxRateNotZero;
  }

  /**
   * 不同场景下balance输入框下面横线的样式
   */
  get lineClass() {
    if (this.taxRateNotZero) {
      if (this.errorText) {
        return 'tax-line-error';
      }
      return 'tax-line';
    }

    if (this.errorText) {
      return 'normal-line-error';
    }
    return 'normal-line';
  }

  get isFF() {
    return browerType.supporter === 'firefox';
  }

  /**
   * 有合同且不是账户禁用的情况下可以充值
   */
  get beAllowed() {
    const status = Number(this.accountInfo?.status);
    return this.hasContract && !ifPaymentDisable(status);
  }

  get needPayTaxes() {
    return this.taxType === paymentTaxType.chargePay;
  }

  get taxRateNotZero() {
    return !isEmpty(this.taxInfo) && Object.values(this.taxInfo).some(rate => rate > 0);
  }

  get recommendedAmountByCountry() {
    // Chile, Peru or Ecuador
    if (this.isCL || this.isPE || this.isEC || this.isCO) {
      if (this.currency === 'USD') {
        return recommendedAmountSelectedCountryUSD;
      } else if (this.currency === 'CLP') {
        return recommendedAmountCLCLP;
      } else if (this.currency === 'PEN') {
        return recommendedAmountPEPEN;
      } else if (this.currency === 'COP') {
        return recommendedAmountCOCOP;
      }
    }
    // other countries
    return recommendedAmount;
  }

  get recommends(): any {
    return this.recommendedAmountByCountry.map(num => {
      let value = num * (this.rechargeAmount.min || 1);
      return {
        value,
        label: formatNumberByLangCodeUtil(value),
      };
    });
  }

  get rechargeAmount(): any {
    let { country, currency } = this.accountInfo;
    let countryUpperCase = country.toUpperCase();
    const rechargeAmount = (countryRecharge[countryUpperCase] || countryRecharge.US)[currency];
    return rechargeAmount || [];
  }

  get curCountry() {
    return this.accountInfo?.country?.toUpperCase();
  }

  get isAE() {
    return this.curCountry === 'AE';
  }

  get isTR() {
    return this.curCountry === 'TR';
  }

  get isSG() {
    return this.curCountry === 'SG';
  }

  get isVN() {
    return this.curCountry === 'VN';
  }

  get isKH() {
    return this.curCountry === 'KH';
  }

  get isCH() {
    return this.curCountry === 'CH';
  }

  get isZA() {
    return this.curCountry === 'ZA';
  }

  get isNO() {
    return this.curCountry === 'NO';
  }

  get isMX() {
    return this.curCountry === 'MX';
  }

  get isCL() {
    return this.curCountry === 'CL';
  }

  get isPE() {
    return this.curCountry === 'PE';
  }

  get isEC() {
    return this.curCountry === 'EC';
  }

  get isCO() {
    return this.curCountry === 'CO';
  }

  get isEG() {
    return this.curCountry === 'EG';
  }

  get isCA() {
    return this.curCountry === 'CA';
  }

  get taxTipDesc() {
    let descKey = '';
    if (this.isAE) {
      descKey = 'moudule_payment_uae_tax_tip';
    } else if (this.isTR) {
      descKey = 'module_payment_tr_tax_tip';
    } else if (this.isSG) {
      descKey = 'module_payment_sg_tax_tip';
    } else if (this.isVN) {
      descKey = 'module_payment_vn_tax_tip';
    } else if (this.isKH) {
      descKey = 'module_payment_kh_tax_tip';
    } else if (this.isCH) {
      descKey = 'module_payment_ch_tax_tip';
    } else if (this.isZA) {
      descKey = 'module_payment_za_tax_tip';
    } else if (this.isNO) {
      descKey = 'module_payment_no_tax_tip';
    } else if (this.isMX) {
      descKey = 'module_payment_mx_tax_tip';
    } else if (this.isCL) {
      descKey = 'module_payment_cl_tax_tip';
    } else if (this.isCO) {
      descKey = 'module_payment_co_tax_tip';
    } else if (this.isEG) {
      descKey = 'module_payment_eg_tax_tip';
    } else if (this.isCA) {
      descKey = 'module_payment_ca_tax_tip';
    }
    return this.$t(descKey);
  }

  get queryParam(): string {
    let redirectInfo = '';
    if (this.isNotBcChildSelfAccount && this.$route.query?.from) {
      redirectInfo = `&redirect=${encodeURIComponent('/wap/budget?from=payment')}&cancel_redirect=${encodeURIComponent(
        '/wap/purchase?from=payment',
      )}`;
    }
    return `&language=${getLang()}&newCreate=${Number(this.$route.query?.newCreate) ? 1 : 0}${redirectInfo}`;
  }

  getIsShowTaxHelpTip(rate: number, taxName: string) {
    return (
      (this.isAE && Number(rate) === 5) ||
      (this.isTR && Number(rate) === 18) ||
      (this.isSG && Number(rate) === 7) ||
      ((this.isVN ||
        this.isKH ||
        this.isCH ||
        this.isZA ||
        this.isNO ||
        this.isMX ||
        this.isCL ||
        this.isCO ||
        this.isEG ||
        this.isCA) &&
        Number(rate) > 0 &&
        !this.noTipTaxKey.includes(taxName))
    );
  }

  /**
   * 设置实时税值
   */

  // 检查金额是否正确
  checkAmount(amount: number | string) {
    let msg;
    if (amount) {
      msg = this.validateCurrencyAmount(amount);
      if (!msg) {
        let { min, max } = this.rechargeAmount;
        if (amount === '' || amount < min) {
          msg = this.$t('module_purchase_amount_min', {
            amount: formatNumberByLangCodeUtil(min),
            currency: this.currency,
          });
        } else if (max && amount > max) {
          msg = this.$t('module_purchase_amount_max', {
            amount: formatNumberByLangCodeUtil(max),
            currency: this.currency,
          });
        }
      }
    }
    return msg;
  }

  clickMore() {
    if (!this.paying) {
      this.$trackEvent(TeaConfigPayment.payment_lite_more, {
        payment_type: 1,
      });
      this.showManagerCard = true;
    }
  }
  hideManageCard() {
    this.$trackEvent(TeaConfigPayment.payment_lite_manage_cancel, {
      payment_type: 1,
    });
    this.showManagerCard = false;
  }
  reportGoHelp() {
    this.$trackEvent(TeaConfigPayment.payment_lite_help, {
      payment_type: 1,
    });
  }
  async onPurchase() {
    this.$trackEvent(TeaConfigPayment.payment_lite_prepay_add_balance);
    if (!canEdit() || isOperatorOfAgent()) {
      this.$toast({
        message: this.$t('common_account_no_authority'),
      });
      return;
    }
    if (!this.beAllowed) {
      this.$toast({
        message: this.$t('module_purchase_not_be_allowed'),
      });
      return;
    }
    if (!this.amount) {
      return;
    }

    const msg = this.checkAmount(this.amount);
    if (msg) {
      this.errorText = msg;
      return;
    }

    this.paying = true;
    this.$Loading.show();
    try {
      const currentDomain = window.location.host;
      const {
        data: { form_html },
      } = await this.subPrePay({
        amount: this.amount,
        upay_route: 1, // 判定用新收银台还是老收银台，老收银台0 新收银台1
        ad_channel: PaymentSource.TTAM_MOBILE_LITE,
        scene: currentDomain === domainTTP ? PAYMENT_SCENE.TTP_MOBILE : PAYMENT_SCENE.DEFAULT, // distinguish ttp scene
      });
      this.$Loading.hide();
      tool.autoGo(`${form_html}${this.queryParam}`);
    } finally {
      this.paying = false;
    }
  }

  onBack(): void {
    // 新用户payment页面cancel回dashboard，其余用户回setting
    // 收银台跳转回来新用户newCreate=1，老用户newCreate=0
    this.$trackEvent(TeaConfigPayment.payment_lite_prepay_cancel);
    if (this.$route.query.from) {
      this.$router.push({
        name: 'budget',
        query: { aadvid: this.aadvid, from: 'purchase' },
      });
    } else if (Number(this.$route.query.newCreate)) {
      this.$router.push({
        name: 'perf-dashboard-detail',
        query: { aadvid: this.aadvid },
      });
    } else {
      this.$router.push({
        name: 'setting',
        query: { aadvid: this.aadvid },
      });
    }
  }

  manageCard() {
    this.$trackEvent(TeaConfigPayment.payment_lite_manage, {
      payment_type: 1,
    });
    this.showManagerCard = false;
    this.getCardBindLink().then(url => {
      window.location.href = `${url}${this.queryParam}`;
    });
  }

  // 当用户滚动屏幕时清除 field 的焦点
  handleTouchMove = (e: TouchEvent) => {
    if (this.$refs.field?.$el?.compareDocumentPosition(e.target) & Node?.DOCUMENT_POSITION_CONTAINED_BY) {
      // 输入框内光标拖动不隐藏键盘
      return;
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };
  handleFieldFocus() {
    window.addEventListener('touchmove', this.handleTouchMove);
  }
  handleFieldBlur() {
    this.$trackEvent(TeaConfigPayment.payment_lite_prepay_input_tax, {
      payment_tax: this.showTaxDetail ? 1 : 2,
    });
    window.removeEventListener('touchmove', this.handleTouchMove);
  }
  toggleTaxTipModal() {
    this.showTaxTip = !this.showTaxTip;
  }
  mounted(): void {
    if (this.isManagementByBC || this.isSelfBcAccount) {
      navigateToCreationObjectives(this.$route.query as Record<string, string>, this.$router.push.bind(this.$router));
    }
  }
}
